import _extends from "@babel/runtime/helpers/extends";
import _inheritsLoose from "@babel/runtime/helpers/inheritsLoose";
import React from "react";
import PropTypes from "prop-types";
import { setStylesAndCreateResetter } from "./utils";
import flipNode from "./flipNode";
var defaultHandlerOpts = {
  positionMode: "transform",
  // none | transform
  scaleMode: "transform",
  // none | transform | transform-no-children
  transitionProps: [],
  setWillChange: false,
  delayMs: 0
};

var FlipGroup = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(FlipGroup, _React$Component);

  function FlipGroup() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _React$Component.call.apply(_React$Component, [this].concat(args)) || this;
    _this.nodeInfoPerKey = {};

    _this.getOrCreateHandlerForKey = function (key, userOpts) {
      var opts = _extends({}, defaultHandlerOpts, userOpts);

      var existing = _this.nodeInfoPerKey[key];

      if (existing) {
        existing.opts = opts;
        return opts._passInfo ? existing : existing.handler;
      }

      var newVal = {
        key: key,
        node: null,
        handler: function handler(node) {
          var _this$props = _this.props,
              durationMs = _this$props.durationMs,
              timingFunction = _this$props.timingFunction;
          if (newVal.currentTransition) newVal.currentTransition.clearTimeout();
          newVal.node = node;

          if (node) {
            if (process.env.NODE_ENV !== "production") {
              var cStyle = getComputedStyle(node, null);
              var existingTransition = cStyle.getPropertyValue("transition") || "none";

              if (!existingTransition.match(/^(none|\S+\s+0s\s+\S+\s+0s\b)/)) {
                // eslint-disable-next-line no-console
                console.warn("Found user-defined transition \"" + existingTransition + "\" on\b", node, '\nThis will be overwritten by react-flip-primitives. Use `registerNode(key, {transitionProps: ["opacity" , ...]})` instead!');
              }
            }

            node.style.transition = opts.transitionProps.map(function (prop) {
              return prop + " " + durationMs + "ms " + timingFunction + " " + opts.delayMs + "ms";
            }).join(", ");
          }
        },
        opts: opts,
        currentTransition: null
      };
      _this.nodeInfoPerKey[key] = newVal;
      return opts._passInfo ? newVal : newVal.handler;
    };

    _this.registerNode = function (key, opts) {
      if (opts === void 0) {
        opts = {};
      }

      return _this.getOrCreateHandlerForKey(key, opts);
    };

    return _this;
  }

  var _proto = FlipGroup.prototype;

  _proto.getSnapshotBeforeUpdate = function getSnapshotBeforeUpdate(prevProps) {
    if (prevProps.changeKey === this.props.changeKey) return null;
    var measuredNodes = {};
    var nodeInfos = Object.values(this.nodeInfoPerKey);
    nodeInfos.forEach(function (_ref) {
      var key = _ref.key,
          node = _ref.node;
      if (node) measuredNodes[key] = node.getBoundingClientRect();
    });
    nodeInfos.forEach(function (nodeInfo) {
      if (nodeInfo.currentTransition) {
        nodeInfo.currentTransition.clearTimeout();
        nodeInfo.currentTransition = null;
      }
    });
    return measuredNodes;
  };

  _proto.isEnteringNode = function isEnteringNode(nodeInfo, measuredNodes) {
    if (nodeInfo.opts.isEnteringWithStyles) {
      if (measuredNodes[nodeInfo.key]) {
        // eslint-disable-next-line no-console
        console.warn("'" + nodeInfo.key + "' is set as 'isEntering' even though it's measured already!?");
        return false;
      }

      return true;
    }

    return false;
  };

  _proto.isLeavingNode = function isLeavingNode(nodeInfo) {
    if (nodeInfo.opts.isLeaving) {
      return true;
    } else {
      if (nodeInfo.leaving) {
        nodeInfo.leaving.abortLeaving();
      }

      return false;
    }
  };

  _proto.styleEntering = function styleEntering(measuredNodes, nodes) {
    if (!nodes.length) return function () {};
    var resetStyles = [];
    var newPositions = [];
    nodes.forEach(function (nodeInfo) {
      resetStyles.push(setStylesAndCreateResetter(nodeInfo.node, {
        transition: "none"
      }));
      var _nodeInfo$opts$isEnte = nodeInfo.opts.isEnteringWithStyles,
          decorationStyle = _nodeInfo$opts$isEnte.decorationStyle,
          positionStyle = _nodeInfo$opts$isEnte.positionStyle;

      if (decorationStyle) {
        resetStyles.push(setStylesAndCreateResetter(nodeInfo.node, decorationStyle));
      }

      if (positionStyle) {
        var rect = nodeInfo.node.getBoundingClientRect();
        var cStyle = getComputedStyle(nodeInfo.node, null);
        var marginTop = parseInt(cStyle.getPropertyValue("margin-top"), 10);
        var marginLeft = parseInt(cStyle.getPropertyValue("margin-left"), 10);
        newPositions.push({
          nodeInfo: nodeInfo,
          style: _extends({
            width: rect.width,
            height: rect.height
          }, positionStyle, {
            top: nodeInfo.node.offsetTop - marginTop,
            left: nodeInfo.node.offsetLeft - marginLeft,
            position: "absolute"
          })
        });
      }
    });
    var resetPositions = newPositions.map(function (_ref2) {
      var nodeInfo = _ref2.nodeInfo,
          style = _ref2.style;
      return setStylesAndCreateResetter(nodeInfo.node, style);
    });
    nodes.forEach(function (_ref3) {
      var key = _ref3.key,
          node = _ref3.node;
      measuredNodes[key] = node.getBoundingClientRect();
    });
    resetPositions.forEach(function (reset) {
      return reset();
    });
    return function () {
      return resetStyles.forEach(function (reset) {
        return reset();
      });
    };
  };

  _proto.styleLeavingAndRemoveFromFlow = function styleLeavingAndRemoveFromFlow(nodes, measuredNodes) {
    var _this2 = this;

    if (!nodes.length) return;
    var newPositions = [];
    nodes.forEach(function (nodeInfo) {
      var rect = measuredNodes[nodeInfo.key];
      var cStyle = getComputedStyle(nodeInfo.node, null);
      var marginTop = parseInt(cStyle.getPropertyValue("margin-top"), 10);
      var marginLeft = parseInt(cStyle.getPropertyValue("margin-left"), 10);
      newPositions.push({
        nodeInfo: nodeInfo,
        style: _extends({
          width: rect.width,
          height: rect.height
        }, nodeInfo.opts.isLeaving.finalStyle, {
          top: nodeInfo.node.offsetTop - marginTop,
          left: nodeInfo.node.offsetLeft - marginLeft,
          position: "absolute"
        })
      });
    });
    newPositions.forEach(function (_ref4) {
      var nodeInfo = _ref4.nodeInfo,
          style = _ref4.style;
      var originalStyle = setStylesAndCreateResetter(nodeInfo.node, style);
      nodeInfo.leaving = {
        onDone: function onDone() {
          nodeInfo.opts.isLeaving.onDone();
          nodeInfo.leaving = null;
          delete _this2.nodeInfoPerKey[nodeInfo.key];
        },
        abortLeaving: function abortLeaving() {
          originalStyle();
          nodeInfo.leaving = null;
        }
      };
    });
  };

  _proto.performUpdate = function performUpdate(measuredNodes) {
    var _this3 = this;

    var newPositions = [];
    var _this$props2 = this.props,
        durationMs = _this$props2.durationMs,
        timingFunction = _this$props2.timingFunction;
    var enteringNodes = [];
    var leavingNodes = [];
    var nodeInfos = Object.values(this.nodeInfoPerKey);
    nodeInfos.forEach(function (nodeInfo) {
      if (_this3.isEnteringNode(nodeInfo, measuredNodes)) {
        enteringNodes.push(nodeInfo);
      } else if (_this3.isLeavingNode(nodeInfo)) {
        leavingNodes.push(nodeInfo);
      }
    });
    this.styleLeavingAndRemoveFromFlow(leavingNodes, measuredNodes);
    var resetEnterStyles = this.styleEntering(measuredNodes, enteringNodes);
    nodeInfos.forEach(function (nodeInfo) {
      if (nodeInfo.node && measuredNodes[nodeInfo.key]) {
        newPositions.push({
          nodeInfo: nodeInfo,
          prevRect: measuredNodes[nodeInfo.key],
          currentRect: nodeInfo.node.getBoundingClientRect()
        });
      }
    });
    var nextFrameActions = newPositions.map(function (p) {
      return {
        resetFlipStyles: flipNode(p, {
          durationMs: durationMs,
          timingFunction: timingFunction
        }),
        key: p.nodeInfo.key
      };
    });

    if (nextFrameActions.length) {
      // asking for two animation frames since one frame is sometimes not enough to trigger transitions
      requestAnimationFrame(function () {
        return requestAnimationFrame(function () {
          return nextFrameActions.forEach(function (_ref5) {
            var resetFlipStyles = _ref5.resetFlipStyles;
            if (resetFlipStyles) resetFlipStyles();
          });
        }, resetEnterStyles());
      });
    }
  };

  _proto.componentDidMount = function componentDidMount() {// this.performUpdate({})
  };

  _proto.componentDidUpdate = function componentDidUpdate(prevProps, prevState, measuredNodes) {
    if (prevProps.changeKey === this.props.changeKey) return;
    this.performUpdate(measuredNodes);
  };

  _proto.render = function render() {
    return this.props.children(this.registerNode);
  };

  return FlipGroup;
}(React.Component);

FlipGroup.defaultProps = {
  durationMs: 200,
  timingFunction: "ease-in-out"
};
export { FlipGroup as default };
process.env.NODE_ENV !== "production" ? FlipGroup.propTypes = {
  durationMs: PropTypes.number,
  timingFunction: PropTypes.string,
  changeKey: PropTypes.any.isRequired,
  children: PropTypes.func.isRequired
} : void 0;